import React, { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md'
import api from '../../../../Services/api'
import { Loader } from '../../../../Components/Loader'

const PRODUCT_PRODUCTION_TYPES_ENDPOINT = '/product-production-types'

interface IProductionType {
  id: string
  name: string
  sheet: number
  production: number
  machineProduction: number
  logistics: number
}

export const ProductionTypesHCP: FC = () => {
  const [types, setTypes] = useState<IProductionType[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [sheet, setSheet] = useState(0)
  const [production, setProduction] = useState(0)
  const [machineProduction, setMachineProduction] = useState(0)
  const [logistics, setLogistics] = useState(0)
  const [personalizable] = useState(false)

  const fetchTypes = async () => {
    setLoading(true)
    try {
      const res = await api.get(PRODUCT_PRODUCTION_TYPES_ENDPOINT)
      const list = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
      setTypes(list)
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar tipos de producao')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTypes()
  }, [])

  const handleOpen = (type?: IProductionType) => {
    if (type) {
      setEditingId(type.id)
      setName(type.name)
      setSheet(type.sheet || 0)
      setProduction(type.production || 0)
      setMachineProduction(type.machineProduction || 0)
      setLogistics(type.logistics || 0)
    } else {
      setEditingId(null)
      setName('')
      setSheet(0)
      setProduction(0)
      setMachineProduction(0)
      setLogistics(0)
    }
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setEditingId(null)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = {
        name,
        sheet,
        production,
        machineProduction,
        logistics,
        personalizable,
      }

      if (editingId) {
        await api.put(`${PRODUCT_PRODUCTION_TYPES_ENDPOINT}/${editingId}`, payload)
      } else {
        await api.post(PRODUCT_PRODUCTION_TYPES_ENDPOINT, payload)
      }
      handleClose()
      fetchTypes()
    } catch (err: any) {
      alert('Erro ao salvar: ' + (err?.response?.data?.message || err.message))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string, typeName: string) => {
    if (!window.confirm(`Tem certeza que deseja remover o tipo "${typeName}"? Os produtos vinculados ficarao sem referencia de Producao.`)) return
    try {
      await api.delete(`${PRODUCT_PRODUCTION_TYPES_ENDPOINT}/${id}`)
      fetchTypes()
    } catch (err: any) {
      alert('Erro ao excluir: ' + (err?.response?.data?.message || err.message))
    }
  }

  const totalPrazo = sheet + production + machineProduction + logistics

  if (loading) return <Loader />

  return (
    <Box sx={{ p: 1 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Tipos de Produção
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Gerencie opções mapeadas de fluxos (Cartela, Produção, Produção de áquina e Logística).
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<MdAdd />} onClick={() => handleOpen()} size="small">
            NOVO TIPO
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome do Tipo</TableCell>
              <TableCell align="center">Cartela (F2)</TableCell>
              <TableCell align="center">Produção (F3)</TableCell>
              <TableCell align="center">Produção de Máquina (F4)</TableCell>
              <TableCell align="center">Logística (F5)</TableCell>
              <TableCell align="center">Total Calculado</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">Nenhum tipo de produção cadastrado.</TableCell>
              </TableRow>
            )}
            {types.map((t) => {
              const total = (t.sheet || 0) + (t.production || 0) + (t.machineProduction || 0) + (t.logistics || 0)
              return (
                <TableRow key={t.id}>
                  <TableCell><Typography fontWeight={600}>{t.name}</Typography></TableCell>
                  <TableCell align="center">{t.sheet || 0}</TableCell>
                  <TableCell align="center">{t.production || 0}</TableCell>
                  <TableCell align="center">{t.machineProduction || 0}</TableCell>
                  <TableCell align="center">{t.logistics || 0}</TableCell>
                  <TableCell align="center">
                    <Typography color="primary" fontWeight={700}>{total} dias</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpen(t)}><MdEdit /></IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => handleDelete(t.id, t.name)} color="error"><MdDelete /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>{editingId ? 'Editar Tipo de Produção' : 'Novo Tipo de Produção'}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {editingId && (
            <Alert severity="warning">
              Atualizar valores de dias aqui vai recalcular todos os produtos vinculados da sua base.
            </Alert>
          )}

          <Alert severity="info">
            Os fluxos SHEET (Cartela) e PRODUCTION (Produção) são apenas para produtos personalizados ou itens em pedidos de reposição.
          </Alert>

          <TextField
            label="Nome da Categoria"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
          />

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', mb: 1.5, display: 'block' }}>
              Composicao de Prazos
            </Typography>
            <TextField
              label="Prazo Base Total (Soma)"
              type="number"
              value={totalPrazo}
              disabled
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />
            <Grid2 container spacing={2}>
              <Grid2 size={4}><TextField label="F1: Arte" type="number" value={0} disabled fullWidth size="small" /></Grid2>
              <Grid2 size={4}><TextField label="F2: Cartela" type="number" value={sheet} onChange={e => setSheet(Number(e.target.value) < 0 ? 0 : Number(e.target.value))} fullWidth size="small" /></Grid2>
              <Grid2 size={4}><TextField label="F3: Produção" type="number" value={production} onChange={e => setProduction(Number(e.target.value) < 0 ? 0 : Number(e.target.value))} fullWidth size="small" /></Grid2>
              <Grid2 size={6}><TextField label="F4: Produção de máquina" helperText="Adicionar dias somente se o produto for máquina; caso contrário, deixe 0." type="number" value={machineProduction} onChange={e => setMachineProduction(Number(e.target.value) < 0 ? 0 : Number(e.target.value))} fullWidth size="small" /></Grid2>
              <Grid2 size={6}><TextField label="F5: Logística" type="number" value={logistics} onChange={e => setLogistics(Number(e.target.value) < 0 ? 0 : Number(e.target.value))} fullWidth size="small" /></Grid2>
            </Grid2>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleClose} color="inherit">Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !name.trim()}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar Categoria'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
